import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import { ICinemaPalette } from '../palette';
import { MiniGameId } from './types';
import { pickableGames, TUTORIAL_ID, tutorialOffered } from './catalog';
import { isTouchPrimary } from './gameOptions';
import { WasdGlyph } from './gameIcons';
import GamePicker, { opensUpward, PICKER_ID } from './GamePicker';
import { getLauncherStyles } from './launcherStyles';
import { miniGameGlobalStyles } from './styleTokens';
import {
  MINI_GAMES_PLAY,
  MINI_GAMES_TUTORIAL,
  MINI_GAMES_TUTORIAL_BADGE,
} from './constants';

interface IMiniGamesDockProps {
  palette: ICinemaPalette;
  percent: number;
  statusLine: string | null;
  openId: MiniGameId | null;
  onOpen: (id: MiniGameId) => void;
}

const MiniGamesDock = ({
  palette,
  percent,
  statusLine,
  openId,
  onOpen,
}: IMiniGamesDockProps) => {
  const classes = useMemo(() => getLauncherStyles(palette), [palette]);
  const touch = useMemo(() => isTouchPrimary(), []);
  const games = useMemo(() => pickableGames(touch), [touch]);
  const playRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const refocusRef = useRef(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [up, setUp] = useState(true);

  useEffect(() => {
    const opener = openerRef.current;

    if (openId !== null || !opener) {
      return;
    }

    openerRef.current = null;
    opener.focus({ preventScroll: true });
  }, [openId]);

  const launch = (id: MiniGameId, opener: HTMLElement | null) => {
    openerRef.current = opener;
    onOpen(id);
  };

  const showPicker = (event: React.MouseEvent<HTMLElement>) => {
    const button = event.currentTarget;
    setUp(opensUpward(button.getBoundingClientRect(), window.innerHeight));
    setAnchor(button);
  };

  const dismiss = (returnFocus: boolean) => {
    refocusRef.current = returnFocus;
    setAnchor(null);
  };

  const pickerGone = () => {
    if (refocusRef.current) {
      refocusRef.current = false;
      playRef.current?.focus({ preventScroll: true });
    }
  };

  const pick = (id: MiniGameId) => {
    setAnchor(null);
    launch(id, playRef.current);
  };

  return (
    <Box sx={classes.dock} data-testid="myta-cinema-minigames">
      <GlobalStyles styles={miniGameGlobalStyles} />
      <Box
        component="button"
        type="button"
        ref={playRef}
        sx={[classes.primary, !!anchor && classes.primaryOpen]}
        onClick={showPicker}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : 'false'}
        aria-controls={anchor ? PICKER_ID : undefined}
        data-testid="myta-cinema-minigames-play"
      >
        <SportsEsportsOutlinedIcon />
        {MINI_GAMES_PLAY}
      </Box>
      {tutorialOffered(touch) ? (
        <Box
          component="button"
          type="button"
          sx={classes.ghost}
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            launch(TUTORIAL_ID, event.currentTarget)
          }
          data-testid="myta-cinema-minigames-tutorial"
        >
          <WasdGlyph accent={palette.accentHi} />
          {MINI_GAMES_TUTORIAL}
          <Box
            component="span"
            sx={classes.itemBadge}
            data-testid="myta-cinema-minigames-tutorial-badge"
          >
            {MINI_GAMES_TUTORIAL_BADGE}
          </Box>
        </Box>
      ) : null}
      <GamePicker
        anchor={anchor}
        up={up}
        classes={classes}
        accent={palette.accentHi}
        games={games}
        percent={percent}
        statusLine={statusLine}
        onPick={pick}
        onDismiss={dismiss}
        onExited={pickerGone}
      />
    </Box>
  );
};

export default MiniGamesDock;
